/*
================================================================================
File: src/components/navigation/Navbar.js
Description: Main navigation sidebar for the application.
================================================================================
*/
import React, { useState, useCallback } from "react";
import { useAdaptiveMMKV } from "../../hooks/useAdaptiveMMKV";
import { storage } from "../../App";
import { views } from "../../constants/views";
import instance from "../../api/axios";

const NavItem = ({ label, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 my-2 transition-all duration-200 rounded-lg group ${
      active
        ? "bg-blue-600 text-white shadow-md"
        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50"
    }`}
  >
    <span
      className={`p-2 rounded-lg flex-shrink-0 transition-colors ${
        active
          ? "bg-blue-700 text-white"
          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-700"
      }`}
    >
      <span className="w-5 h-5 flex items-center justify-center">
        {icon}
      </span>
    </span>
    <span className={`font-medium text-sm ${active ? "font-semibold" : ""}`}>
      {label}
    </span>
  </button>
);

const SubNavItem = ({ label, active, view, onViewChange }) => {
  // Check if the view is an external URL
  const isExternal = view.startsWith("http");

  const handleClick = () => {
    if (isExternal) {
      // Open external links in a new tab
      window.open(view, "_blank", "noopener,noreferrer");
    } else {
      // Handle internal view changes
      onViewChange(view);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full flex items-center px-4 py-2 pl-14 text-sm transition-colors duration-200 rounded gap-2 ${
        active
          ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold border-l-2 border-blue-500"
          : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/30 hover:text-blue-600 dark:hover:text-blue-400"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"}`}></span>
      {label}
    </button>
  );
};

const Navbar = ({ onViewChange, currentView, user }) => {
  const [activeSubMenu, setActiveSubMenu] = useState("");
  const [isNavbarOpen, setIsNavbarOpen] = useState(true);
  const [lastView] = useAdaptiveMMKV("user.Last.Sub.View", storage);

  const pulldata = useCallback(async (userId) => {
    const response = await instance.get(`/cloudUserData.php?u=${userId}`);
    storage.set("user.Feed", JSON.stringify(response.data[0]));
  }, []);

  const toggleSubMenu = async (menu) => {
    try {
      await pulldata(user?.app_id);
    } catch (error) {
      console.error("Error loading user data:", error);
    }
    setActiveSubMenu(activeSubMenu === menu ? "" : menu);
  };

  // SVG Icons
  const lock = (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.356 3.066a1 1 0 0 0-.712 0l-7 2.666A1 1 0 0 0 4 6.68a17.695 17.695 0 0 0 2.022 7.98 17.405 17.405 0 0 0 5.403 6.158 1 1 0 0 0 1.15 0 17.406 17.406 0 0 0 5.402-6.157A17.694 17.694 0 0 0 20 6.68a1 1 0 0 0-.644-.949l-7-2.666Z" />
    </svg>
  );
  const home = (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path
        fillRule="evenodd"
        d="M11.293 3.293a1 1 0 0 1 1.414 0l6 6 2 2a1 1 0 0 1-1.414 1.414L19 12.414V19a2 2 0 0 1-2 2h-3a1 1 0 0 1-1-1v-3h-2v3a1 1 0 0 1-1 1H7a2 2 0 0 1-2-2v-6.586l-.293.293a1 1 0 0 1-1.414-1.414l2-2 6-6Z"
        clipRule="evenodd"
      />
    </svg>
  );
  const home2 = (
    <svg
      className="w-6 h-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m4 12 8-8 8 8M6 10.5V19a1 1 0 0 0 1 1h3v-3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3h3a1 1 0 0 0 1-1v-8.5"
      />
    </svg>
  );
  const archive = (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path
        fillRule="evenodd"
        d="M20 10H4v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8ZM9 13v-1h6v1a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1Z"
        clipRule="evenodd"
      />
      <path d="M2 6a2 2 0 0 1 2-2h16a2 2 0 1 1 0 4H4a2 2 0 0 1-2-2Z" />
    </svg>
  );
  const investor = (
    <svg
      className="w-6 h-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20.283 8h-4.285m3.85 3h-3.85m4.061-6H11v11h8.27l1.715-9.847A.983.983 0 0 0 20.059 5ZM6.581 13.23h-.838A13.752 13.752 0 0 1 5.622 11c-.02-.745.02-1.49.12-2.23h1.04c.252 0 .496-.088.683-.245a.927.927 0 0 0 .329-.61l.2-1.872a.888.888 0 0 0-.045-.39.936.936 0 0 0-.212-.34 1.017 1.017 0 0 0-.341-.231A1.08 1.08 0 0 0 6.983 5h-2.06a1.27 1.27 0 0 0-.699.204 1.135 1.135 0 0 0-.442.543A15.066 15.066 0 0 0 3.007 11a15.656 15.656 0 0 0 .795 5.229c.165.462 1.342.771 1.864.771h1.116c.142 0 .283-.028.413-.082.13-.053.246-.132.341-.23a.936.936 0 0 0 .212-.34.889.889 0 0 0 .046-.391l-.201-1.873a.927.927 0 0 0-.33-.609 1.059 1.059 0 0 0-.682-.245ZM10 18v1h10v-1a2 2 0 0 0-2-2h-6a2 2 0 0 0-2 2Z"
      />
    </svg>
  );
  const globe = (
    <svg
      className="w-6 h-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        strokeLinecap="round"
        d="M4.37 7.657c2.063.528 2.396 2.806 3.202 3.87 1.07 1.413 2.075 1.228 3.192 2.644 1.805 2.289 1.312 5.705 1.312 6.705M20 15h-1a4 4 0 0 0-4 4v1M8.587 3.992c0 .822.112 1.886 1.515 2.58 1.402.693 2.918.351 2.918 2.334 0 .276 0 2.008 1.972 2.008 2.026.031 2.026-1.678 2.026-2.008 0-.65.527-.9 1.177-.9H20M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  );
  const school = (
    <svg
      className="w-6 h-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m7.4285 11 5-6 5 6m-10 0h-3v8h16v-8h-3m-10 0H3.42851l3-4h4.33739l-3.3374 4Zm10 0V7.5m-3 5.5c0 1.1046-.8954 2-2 2s-2-.8954-2-2 .8954-2 2-2 2 .8954 2 2Zm3-8v3h4V5h-4Z"
      />
    </svg>
  );
  const cart = (
    <svg
      className="w-6 h-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 4h1.5L9 16m0 0h8m-8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-8.5-3h9.25L19 7h-1M8 7h-.688M13 5v4m-2-2h4"
      />
    </svg>
  );
  const linki = (
    <svg
      className="w-6 h-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.213 9.787a3.391 3.391 0 0 0-4.795 0l-3.425 3.426a3.39 3.39 0 0 0 4.795 4.794l.321-.304m-.321-4.49a3.39 3.39 0 0 0 4.795 0l3.424-3.426a3.39 3.39 0 0 0-4.794-4.795l-1.028.961"
      />
    </svg>
  );
  const support = (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18 17h-.09c.058-.33.088-.665.09-1v-1h1a1 1 0 0 0 0-2h-1.09a5.97 5.97 0 0 0-.26-1H17a2 2 0 0 0 2-2V8a1 1 0 1 0-2 0v2h-.54a6.239 6.239 0 0 0-.46-.46V8a3.963 3.963 0 0 0-.986-2.6l.693-.693A1 1 0 0 0 16 4V3a1 1 0 1 0-2 0v.586l-.661.661a3.753 3.753 0 0 0-2.678 0L10 3.586V3a1 1 0 1 0-2 0v1a1 1 0 0 0 .293.707l.693.693A3.963 3.963 0 0 0 8 8v1.54a6.239 6.239 0 0 0-.46.46H7V8a1 1 0 0 0-2 0v2a2 2 0 0 0 2 2h-.65a5.97 5.97 0 0 0-.26 1H5a1 1 0 0 0 0 2h1v1a6 6 0 0 0 .09 1H6a2 2 0 0 0-2 2v2a1 1 0 1 0 2 0v-2h.812A6.012 6.012 0 0 0 11 21.907V12a1 1 0 0 1 2 0v9.907A6.011 6.011 0 0 0 17.188 19H18v2a1 1 0 0 0 2 0v-2a2 2 0 0 0-2-2Zm-4-8.65a5.922 5.922 0 0 0-.941-.251l-.111-.017a5.52 5.52 0 0 0-1.9 0l-.111.017A5.925 5.925 0 0 0 10 8.35V8a2 2 0 1 1 4 0v.35Z" />
    </svg>
  );
  const settings = (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path
        fillRule="evenodd"
        d="M17 10v1.126c.367.095.714.24 1.032.428l.796-.797 1.415 1.415-.797.796c.188.318.333.665.428 1.032H21v2h-1.126c-.095.367-.24.714-.428 1.032l.797.796-1.415 1.415-.796-.797a3.979 3.979 0 0 1-1.032.428V20h-2v-1.126a3.977 3.977 0 0 1-1.032-.428l-.796.797-1.415-1.415.797-.796A3.975 3.975 0 0 1 12.126 16H11v-2h1.126c.095-.367.24-.714.428-1.032l-.797-.796 1.415-1.415.796.797A3.977 3.977 0 0 1 15 11.126V10h2Zm.406 3.578.016.016c.354.358.574.85.578 1.392v.028a2 2 0 0 1-3.409 1.406l-.01-.012a2 2 0 0 1 2.826-2.83ZM5 8a4 4 0 1 1 7.938.703 7.029 7.029 0 0 0-3.235 3.235A4 4 0 0 1 5 8Zm4.29 5H7a4 4 0 0 0-4 4v1a2 2 0 0 0 2 2h6.101A6.979 6.979 0 0 1 9 15c0-.695.101-1.366.29-2Z"
        clipRule="evenodd"
      />
    </svg>
  );

  const navItems = [
    ...(user?.op === "1"
      ? [{ label: "Admin", icon: lock, view: views.admin }]
      : []),
    { label: "Dashboard", icon: home, view: views.dashboard },
    {
      label: "History",
      icon: archive,
      view: views.history,
      subItems: [
        { label: "Skiptraces", view: "skipTraceHistory" },
        { label: "AI Querys", view: "leadQueryHistory" },
      ],
    },
    {
      label: "Seller Leads",
      icon: home2,
      view: views.seller,
      subItems: [
        { label: "All Leads", view: "sellerleads" },
        { label: "Autoresponders", view: "sellerresponders" },
        { label: "Skiptrace", view: "skiptrace" },
        { label: "Quick Scoreboard", view: "sellerQS" },
      ],
    },
    {
      label: "Investor Leads",
      icon: investor,
      view: views.investor,
      subItems: [
        { label: "All Leads", view: "investorleads" },
        { label: "Autoresponders", view: "investorresponders" },
      ],
    },
    { label: "Squeeze Site", icon: globe, view: views.sellerSites },
    { label: "Training", icon: school, view: views.training },
    { label: "E-Commerce", icon: cart, view: views.ecommerce },
    {
      label: "Profile Settings",
      icon: settings,
      view: views.settings,
      subItems: [
        { label: "Profile Information", view: "myInformation" },
        { label: "Company Profile", view: "myCompany" },
        { label: "Contract Options", view: "contractOptions" },
        { label: "Investor Profile", view: "myInvestorProfile" },
      ],
    },
    {
      label: "External Links",
      icon: linki,
      view: views.links,
      subItems: [
        {
          label: "Facebook Group",
          view: "https://www.facebook.com/groups/310373962425940/",
        },
        { label: "Calendar", view: "http://www.bigdogcalendar.com/" },
        {
          label: "Canis Major Incubator",
          view: "http://www.canismajorincubator.com/",
        },
        { label: "Alan Ceshker's Office", view: "https://www.ceshker.com/" },
        {
          label: "TREC",
          view: "http://www.trec.texas.gov/agency-information/contracts/",
        },
        { label: "Money for REI", view: "http://www.moneyforrei.com/" },
        { label: "Rent-O-Meter", view: "http://www.rentometer.com/" },
        { label: "Lonestar Land Law", view: "http://www.lonestarlandlaw.com/" },
      ],
    },
    { label: "Site Support", icon: support, view: views.support },
  ];

  return (
    <>
      {/* Floating Toggle Button - appears when navbar is hidden */}
      {!isNavbarOpen && currentView !== "login" && currentView !== "register" && (
        <button
          onClick={() => setIsNavbarOpen(true)}
          className="fixed top-59 left-4 z-50 p-2 rounded-lg text-white transition-colors shadow-lg"
          aria-label="Open navbar"
        >
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        </button>
      )}

      {/* Main Navbar */}
      <div
        className={`h-full bg-white dark:bg-gray-900 shadow-lg w-72 flex flex-col transition-transform duration-300 transform border-r border-gray-200 dark:border-gray-800 ${
          currentView === "login" || currentView === "register"
            ? "hidden"
            : isNavbarOpen
            ? "translate-x-0"
            : "-translate-x-full"
        }`}
      >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 p-6 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3 flex-1">

          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">BigDogTools</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Navigation</p>
          </div>
        </div>
        <button
          onClick={() => setIsNavbarOpen(!isNavbarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Toggle navbar"
        >
         <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        </button>
      </div>

      {/* Navigation Items */}
      <div className="flex-grow overflow-y-auto px-3 py-4">
        {navItems.map((item) => (
          <div key={item.label}>
            <NavItem
              label={item.label}
              icon={item.icon}
              active={
                currentView === item.view ||
                activeSubMenu ===
                  item.view /*|| (lastView && lastView[0].lastView === item.view)*/
              }
              onClick={() => {
                if (item.subItems) {
                  toggleSubMenu(item.view);
                } else {
                  onViewChange(item.view);
                }
              }}
            />
            {item.subItems && activeSubMenu === item.view && (
              <div className="ml-2 border-l-2 border-gray-200 dark:border-gray-700 py-1">
                {item.subItems.map((subItem) => (
                  <SubNavItem
                    key={subItem.view}
                    label={subItem.label}
                    active={
                      currentView === subItem.view ||
                      (lastView && subItem.view === lastView[0].submenu)
                    }
                    view={subItem.view}
                    onViewChange={onViewChange}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      </div>
    </>
  );
};

export default Navbar;
